// src/components/HashtagFilter.tsx
import React, { useMemo } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';

// Define props interface
export interface HashtagFilterProps {
  tasks: Array<{
    tags?: string[];
  }>;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

/**
 * Component for filtering tasks by hashtags
 */
const HashtagFilter: React.FC<HashtagFilterProps> = ({ 
  tasks, 
  selectedTags, 
  onTagsChange 
}) => {
  // Extract all unique hashtags from tasks
  const availableTags = useMemo(() => {
    const allTags = tasks.flatMap(task => task.tags || []);
    return Array.from(new Set(allTags)).sort();
  }, [tasks]);

  // Toggle tag selection
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  // Clear all selected tags
  const clearFilters = () => {
    onTagsChange([]);
  };

  // If no tags, don't render
  if (availableTags.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Tags Filter:</Typography>
        {selectedTags.length > 0 && (
          <Button 
            variant="text" 
            size="small" 
            onClick={clearFilters}
          >
            Clear Filters
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {availableTags.map(tag => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            color={tag === '#urgent' ? 'error' :
                  tag === '#caitriona' ? 'success' :
                  tag === '#priority' ? 'warning' :
                  tag === '#cooler' ? 'info' : 'primary'}
            variant={selectedTags.includes(tag) ? "filled" : "outlined"}
            onClick={() => toggleTag(tag)}
            clickable
          />
        ))}
      </Box>
    </Box>
  );
};

export default HashtagFilter;